import { orpc, useInvalidateRpcQueryFn } from 'src/api/rpc/client';
import { IntegrationKey } from '@erp/rpc';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export function useListIntegrations() {
  return useQuery(orpc.integrations.list.queryOptions());
}

export function useGetIntegration(key: IntegrationKey) {
  return useQuery(orpc.integrations.get.queryOptions({ input: { key } }));
}

export function useTestOracleFusionConnection() {
  return useMutation(
    orpc.integrations.oracleFusion.testConnection.mutationOptions(),
  );
}

export function useTestOracleEbsConnection() {
  return useMutation(
    orpc.integrations.oracleEbs.testConnection.mutationOptions(),
  );
}

export function useTestJiraConnection() {
  return useMutation(orpc.integrations.jira.testConnection.mutationOptions());
}

export function useBeginMicrosoftTeamsConsent() {
  return useMutation(
    orpc.integrations.microsoftTeams.beginAdminConsent.mutationOptions(),
  );
}

export function useTestMicrosoftTeamsConnection() {
  return useMutation(
    orpc.integrations.microsoftTeams.testConnection.mutationOptions(),
  );
}

export function useDisconnectMicrosoftTeams() {
  const invalidateIntegrationsQueries = useInvalidateRpcQueryFn(
    (orpc, { key }: { key: IntegrationKey }) => [
      orpc.integrations.list.key(),
      orpc.integrations.get.key({ input: { key } }),
    ],
  );
  return useMutation(
    orpc.integrations.microsoftTeams.disconnect.mutationOptions({
      onSuccess: () => {
        invalidateIntegrationsQueries({
          key: IntegrationKey.MicrosoftTeams,
        });
      },
    }),
  );
}

export function useUpdateIntegration() {
  const invalidateIntegrationsQueries = useInvalidateRpcQueryFn(
    (orpc, { key }: { key: IntegrationKey }) => [
      orpc.integrations.list.key(),
      orpc.integrations.get.key({ input: { key } }),
    ],
  );
  return useMutation(
    orpc.integrations.update.mutationOptions({
      onSuccess: ({ key }) => {
        invalidateIntegrationsQueries({ key });
      },
    }),
  );
}

export function useFusionSyncState(integrationId: string | undefined) {
  return useQuery({
    ...(integrationId
      ? orpc.integrations.oracleFusion.listSyncState.queryOptions({
          input: { integrationId },
        })
      : { queryKey: orpc.integrations.oracleFusion.listSyncState.key() }),
    enabled: !!integrationId,
  });
}

export function useResetFusionSyncState() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.integrations.oracleFusion.resetSyncState.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.integrations.oracleFusion.listSyncState.key(),
        });
      },
    }),
  );
}
